import React from 'react';
import type { PortailInfo } from '../../../lib/organisateurApi';

export const fmtDateHeure = (iso?: string | null) => {
    if (!iso) return '';
    try { const d = new Date(iso); return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`; } catch { return ''; }
};
export const fmtJour = (iso?: string | null) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }); } catch { return ''; } };

interface Geste { n: number; titre: string; fait: boolean; texte: React.ReactNode; cta: string; onClick: () => void; desactive?: boolean }

/**
 * 19b — les trois gestes dans l'ordre : importer, envoyer, relancer. Une seule action pleine à la fois
 * (le premier geste pas encore fait porte le bouton encre, les autres sont bordés), et chaque geste
 * dit ce qui a été fait et quand. Les règles (menu configuré, une relance par 48 h, plafond) sont
 * celles du serveur ; ici on ne fait que les expliquer avant le clic.
 */
export function OrganisateurGestes({ info, occupe, onImporter, onEnvoyer, onRelancer }: {
    info: PortailInfo;
    occupe: boolean;
    onImporter: () => void;
    onEnvoyer: () => void;
    onRelancer: () => void;
}) {
    const c = info.compteurs;
    const nomEtab = info.etablissement?.name || "l'établissement";
    const relanceBloqueeJusque = info.gestes.relance_possible_le;
    const plafondAtteint = info.plafond.restants <= 0;

    const gestes: Geste[] = [
        {
            n: 1, titre: 'Importer votre liste', fait: c.importes > 0, onClick: onImporter,
            texte: c.importes > 0
                ? <>{c.importes} convive{c.importes > 1 ? 's' : ''} importé{c.importes > 1 ? 's' : ''}{info.gestes.import_le ? ` le ${fmtJour(info.gestes.import_le)}` : ''}. Vous pouvez encore ajouter ou retirer un convive non invité.</>
                : <>Un fichier CSV ou Excel avec <span className="font-mono">Nom</span>, <span className="font-mono">Prénom</span> et <span className="font-mono">Email</span>. Un convive déjà invité n'est jamais dupliqué.</>,
            cta: c.importes > 0 ? 'Remplacer le fichier' : 'Importer le fichier',
        },
        {
            n: 2, titre: 'Envoyer la demande de menu', fait: c.invites > 0 && c.non_invites === 0, onClick: onEnvoyer,
            desactive: c.importes === 0 || !info.menu_pret || c.non_invites === 0 || plafondAtteint,
            texte: !info.menu_pret
                ? <>Le menu n'est pas encore configuré par {nomEtab} : il n'y a rien à demander à vos convives pour l'instant.</>
                : c.invites > 0
                    ? <>Partie le {fmtDateHeure(info.gestes.envoi_le)}, aux {c.invites} adresse{c.invites > 1 ? 's' : ''}. Le courriel est celui de {nomEtab}.{c.non_invites > 0 ? ` ${c.non_invites} convive${c.non_invites > 1 ? 's' : ''} ajouté${c.non_invites > 1 ? 's' : ''} depuis n'ont pas encore reçu la demande.` : ''}</>
                    : <>Chaque convive reçoit un lien personnel pour choisir son plat. Le courriel part à l'en-tête de {nomEtab} ; seul votre mot d'accueil y est inséré.</>,
            cta: c.invites > 0 ? `Renvoyer aux absents${c.non_invites > 0 ? ` (${c.non_invites})` : ''}` : `Envoyer aux ${c.importes} convive${c.importes > 1 ? 's' : ''}`,
        },
        {
            n: 3, titre: c.silencieux > 0 ? `Relancer les ${c.silencieux} silencieux` : 'Relancer les silencieux', fait: c.invites > 0 && c.silencieux === 0, onClick: onRelancer,
            desactive: c.silencieux === 0 || !!relanceBloqueeJusque || plafondAtteint,
            texte: c.invites === 0
                ? <>Après l'envoi, vous pourrez relancer ceux qui n'ont pas répondu — une fois par 48 h.</>
                : c.silencieux === 0
                    ? <>Tout le monde a répondu. Rien à relancer.</>
                    : relanceBloqueeJusque
                        ? <>Dernière relance envoyée le {fmtDateHeure(info.gestes.relance_le)}. Vous pouvez relancer une fois par 48 h : prochaine relance possible le {fmtDateHeure(relanceBloqueeJusque)}.</>
                        : <>{info.gestes.relance_le ? `Dernière relance envoyée le ${fmtJour(info.gestes.relance_le)}. ` : ''}Vous pouvez relancer une fois par 48 h.</>,
            cta: 'Relancer maintenant',
        },
    ];
    // Le bouton plein va au premier geste pas encore fait — « une seule action possible à la fois ».
    const principal = gestes.find((g) => !g.fait && !g.desactive)?.n;

    return (
        <div className="flex flex-col gap-2.5">
            {gestes.map((g) => {
                const main = g.n === principal;
                return (
                    <div key={g.n} className={`rounded-xl bg-surface border ${main ? 'border-content' : 'border-border'} overflow-hidden`}>
                        <div className="flex items-start gap-[13px] px-4 py-[15px] flex-wrap sm:flex-nowrap">
                            <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[12.5px] font-bold ${g.fait ? 'bg-state-ok text-white' : 'bg-content text-surface'}`}>{g.n}</span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-[9px] flex-wrap gap-y-1">
                                    <span className="text-[13.5px] font-bold tracking-[-0.01em] text-content">{g.titre}</span>
                                    <span className={`inline-flex items-center h-[21px] px-[9px] rounded-full border text-[10px] font-bold tracking-[.05em] uppercase whitespace-nowrap ${g.fait ? 'bg-state-ok-soft border-state-ok/30 text-state-ok' : 'bg-state-warn-soft border-state-warn/30 text-state-warn'}`}>{g.fait ? 'Fait' : 'À faire'}</span>
                                </div>
                                <p className="mt-1 text-[12.5px] leading-[1.5] text-content-muted text-pretty">{g.texte}</p>
                            </div>
                            <button type="button" onClick={g.onClick} disabled={occupe || g.desactive}
                                className={`inline-flex items-center gap-[7px] h-9 px-[15px] rounded-full text-[12.5px] font-semibold whitespace-nowrap shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${main ? 'bg-content text-surface hover:opacity-90' : 'bg-surface border border-border text-content hover:border-content'}`}>
                                {g.cta}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
